import { useState, useEffect } from 'react';
import { formatFranc } from '../lib/utils';
import api from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Globe, Shield, Film, Zap, Music, Megaphone, Gamepad2, TrendingUp, Users, Star } from 'lucide-react';
import FrancIcon from './FrancIcon';

const CATEGORY_CONFIG = {
  country_leader: { label: 'Rulers', icon: Globe, color: '#5a2d5e', gradient: 'from-purple-900/20 to-purple-600/5' },
  ceo: { label: 'CEOs', icon: Shield, color: '#1e3a5f', gradient: 'from-blue-900/20 to-blue-600/5' },
  actor: { label: 'Actors', icon: Film, color: '#c97a2e', gradient: 'from-orange-900/20 to-orange-600/5' },
  athlete: { label: 'Athletes', icon: Zap, color: '#2d7d5e', gradient: 'from-green-900/20 to-green-600/5' },
  musician: { label: 'Artists', icon: Music, color: '#b8860b', gradient: 'from-yellow-900/20 to-yellow-600/5' },
  influencer: { label: 'Influencers', icon: Megaphone, color: '#c94070', gradient: 'from-pink-900/20 to-pink-600/5' },
};

export default function CategoryHeroCards({ onCategorySelect, selectedCategory }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/entities/categories/detailed').then(res => {
      setCategories(res.data.categories || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex gap-3 overflow-x-auto pb-2">
        {[1,2,3,4].map(i => <Skeleton key={i} className="h-24 w-40 shrink-0 rounded-xl" />)}
      </div>
    );
  }

  return (
    <div className="flex gap-2.5 overflow-x-auto pb-2 -mx-1 px-1" data-testid="category-hero-cards">
      {/* All button */}
      <button
        onClick={() => onCategorySelect?.('')}
        aria-label="Show all categories"
        aria-pressed={!selectedCategory}
        data-testid="category-card-all"
        className={`shrink-0 w-20 rounded-xl border p-3 text-center transition-all ${
          !selectedCategory
            ? 'bg-primary text-primary-foreground border-primary shadow-sm'
            : 'bg-card border-border/60 hover:border-border/80'
        }`}
      >
        <Star className="h-5 w-5 mx-auto mb-1" />
        <p className="text-[10px] font-semibold">All</p>
      </button>

      {/* Sort categories in fixed order */}
      {['country_leader', 'ceo', 'actor', 'athlete', 'musician', 'influencer']
        .map(catKey => categories.find(c => c._id === catKey))
        .filter(Boolean)
        .map(cat => {
        const config = CATEGORY_CONFIG[cat._id] || {};
        const Icon = config.icon || Star;
        const isSelected = selectedCategory === cat._id;
        
        return (
          <button
            key={cat._id}
            onClick={() => onCategorySelect?.(cat._id)}
            aria-label={`${config.label || cat._id} category, ${cat.count} people, average price ${formatFranc(cat.avg_price)}`}
            aria-pressed={isSelected}
            className={`shrink-0 w-32 rounded-xl border p-3 text-left transition-all ${
              isSelected
                ? 'border-primary/50 shadow-sm bg-primary/5'
                : 'bg-card border-border/60 hover:border-border/80 hover:shadow-sm'
            }`}
            data-testid={`category-card-${cat._id}`}
          >
            <div className="flex items-center gap-1.5 mb-1.5">
              <div
                className="h-6 w-6 rounded-full flex items-center justify-center"
                style={{ backgroundColor: config.color }}
              >
                <Icon className="h-3 w-3 text-white" />
              </div>
              <span className="text-[10px] font-bold">{cat.count}</span>
            </div>
            <p className="text-xs font-semibold truncate">{config.label || cat._id}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-[9px] text-muted-foreground">Avg {formatFranc(cat.avg_price)}</span>
            </div>
            {cat.top_entity && (
              <p className="text-[8px] text-muted-foreground mt-0.5 truncate flex items-center gap-0.5">
                <TrendingUp className="h-2 w-2" /> {cat.top_entity}
              </p>
            )}
          </button>
        );
      })}
    </div>
  );
}
